<template>
  <div class="container mx-auto my-8 px-4 lg:px-0">

    <div class="mb-8 flex flex-wrap flex-col-reverse lg:flex-row justify-between items-center">
      <div class="flex">
        <div class="flex flex-wrap items-stretch relative mr-4 w-80 shadow">
          <div class="flex">
            <span class="flex items-center font-medium leading-normal bg-white border-r border-gray-light px-4 whitespace-no-wrap text-grey-darkest text-sm">
              Sorteer op
            </span>
          </div>
          <select v-model="sortBy" class="flex-shrink flex-grow flex-auto leading-normal w-px flex-1 relative rounded-none appearance-none bg-white px-4 py-3 pr-8  leading-normal text-sm focus:outline-none focus:shadow-outline">
            <option value="name">Naam</option>
            <option value="residence_count">Aantal woningen</option>
            <option value="sources_present_count">Aantal bronnen aanwezig</option>
            <option value="sources_removed_count">Aantal bronnen verwijderd</option>
            <option value="risk">Risico complexen</option>
          </select>
          <div class="pointer-events-none absolute pin-t pin-b pin-r flex items-center px-2 text-gray-700">
            <svg class="fill-current h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20"><path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z"/></svg>
          </div>
        </div>
        <div class="flex flex-wrap items-stretch relative mr-4 w-48 shadow">
          <div class="flex">
            <span class="flex items-center font-medium leading-normal bg-white border-r border-gray-light px-4 whitespace-no-wrap text-grey-darkest text-sm">
              Richting
            </span>
          </div>
          <select v-model="sortDirection" class="flex-shrink flex-grow flex-auto leading-normal w-px flex-1 relative rounded-none appearance-none bg-white px-4 py-3 pr-8  leading-normal text-sm focus:outline-none focus:shadow-outline">
            <option value="">A - Z</option>
            <option value="-">Z - A</option>
          </select>
          <div class="pointer-events-none absolute pin-t pin-b pin-r flex items-center px-2 text-gray-700">
            <svg class="fill-current h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20"><path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z"/></svg>
          </div>
        </div>
      </div>
      <div class="buttons mb-6 lg:mb-0 self-start lg:self-center">
        <button class="leading-none rounded px-8 py-3 text-sm text-white font-semibold" v-if="$complex.isAllowed('matrix')" :disabled="!isSelected" :class="{ 'bg-grey-dark': !isSelected, 'bg-blue-dark': isSelected }" @click="createMatrixFile()">
          Exporteer
        </button>
        <router-link v-if="$complex.isAllowed('create')" :to="{ name: 'complex-new' }" class="btn  ml-4 py-3 text-sm text-white bg-green-dark font-semibold leading-none">
          <span>Nieuw</span>
          <span class="hidden md:inline ml-1">Complex</span>
        </router-link>
      </div>
      <search-input
        class="w-full shadow mt-8"
        placeholder="Zoeken op naam.."
        @searched="onSearched"
        :value="search">
      </search-input>
    </div>

    <div class="bg-white shadow overflow-auto">
      <table class="custom w-full whitespace-no-wrap" ref="table">
        <thead>
          <tr class="text-left text-green-darker">
            <th class="wx-1">
              <input type="checkbox" class="flex items-center ml-4" @click="onSelectAll" :checked="isSelectAll" :indeterminate.prop="isIndeterminate" />
            </th>
            <th class="px-6">Naam</th>
            <th class="px-6 text-right">Aantal woningen</th>
            <th class="px-6 text-right">Aantal bronnen aanwezig</th>
            <th class="px-6 text-right">Aantal bronnen verwijderd</th>
            <th class="px-6" colspan="2"></th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="complex in complexes.data" :key="complex.id" class="hover:bg-grey-lightest text-grey-darker">
            <td class="border-t border-grey-lighter w-1">
              <input type="checkbox" v-model="selected" :value="complex.id" @change="onSelect" class="flex items-center ml-4" />
            </td>
            <td class="border-t border-grey-lighter">
              <button type="button" class="w-full flex items-center px-6 py-4 max-w-xs" @click="goTo({ id: complex.id, label: complex.name })">
                <span class="truncate">{{ complex.name }}</span>
              </button>
            </td>
            <td class="border-t border-grey-lighter">
                <button type="button" class="w-full flex items-center justify-end px-6 py-4" @click="goTo({ id: complex.id, label: complex.name })">
                  {{ complex.residence_count }}
              </button>
            </td>
            <td class="border-t border-grey-lighter">
              <button type="button" class="w-full flex items-center justify-end px-6 py-4" @click="goTo({ id: complex.id, label: complex.name })">
                {{ complex.sources_present_count }}
              </button>
            </td>
            <td class="border-t border-grey-lighter">
              <button type="button" class="w-full flex items-center justify-end px-6 py-4" @click="goTo({ id: complex.id, label: complex.name })">
                {{ complex.sources_removed_count }}
              </button>
            </td>
            <td class="border-t border-grey-lighter w-px">
              <button v-if="$complex.isAllowed('update')" class="px-6 py-4 text-grey-darker outline-none border-0" @click="clicked({ id: complex.id, label: complex.name })">
                <font-awesome-icon :icon="['fas', 'filter']" class="icon" />
              </button>
            </td>
            <td class="border-t border-grey-lighter w-px">
              <button type="button" class="w-full flex items-center px-6 py-4" @click="goTo({ id: complex.id, label: complex.name })">
                <font-awesome-icon :icon="['fas', 'angle-right']" class="icon" />
              </button>
            </td>
          </tr>
          <tr v-if="complexes.data && complexes.data.length === 0">
            <td class="border-t border-grey-lighter px-6 py-4" colspan="6">Geen complexen gevonden.</td>
          </tr>
        </tbody>
      </table>
    </div>

    <base-pagination
      class="mt-2"
      v-if="complexes.meta && complexes.meta.last_page > 1"
      :total-pages="complexes.meta.last_page"
      :total="10"
      :current-page="complexes.meta.current_page"
      @page-changed="onPageChanged">
    </base-pagination>

    <base-notification
      :type="notification.type"
      :message.sync="notification.message"
    ></base-notification>
  </div>
</template>

<script>
import store from '@/store';
import ApiService from '@/common/api.service';
import ComplexPerimeter from '@/perimeters/complex';
import MatrixService from '@/services/matrix';
import SearchInput from '@/components/inputs/Search';
import BaseNotification from '@/components/globals/BaseNotification';

import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';

export default {
  name: 'complex',
  perimeters: [ComplexPerimeter],
  components: {
    FontAwesomeIcon,
    SearchInput,
    BaseNotification
  },
  created() {
    let sort = this.$route.query.sort;

    if (sort && sort.length) {
      const isDesc = sort.indexOf('-') !== -1;

      this.sortBy = isDesc ? sort.substring(1).trim() : sort.trim();
      this.sortDirection = isDesc ? '-' : '';
    }

    this.search = this.$route.query.q;
    this.fetch();
  },
  data() {
    return {
      complexes: {},
      selected: [],
      search: null,
      sortBy: 'name',
      sortDirection: '',
      notification: {
        type: 'info',
        message: ''
      }
    };
  },
  computed: {
    queryString() {
      let query = `?sort=${this.sortDirection}${this.sortBy}`;

      if (this.$route.query.page) {
        query += `&page=${this.$route.query.page}`;
      }

      if (this.$route.query.q || this.search) {
        query += `&filter[name]=${this.search || this.$route.query.q}`;
      }

      return query;
    },

    isSelected() {
      return this.selected.length > 0;
    },

    isIndeterminate() {
      return this.isSelected && !this.isSelectAll;
    },

    isSelectAll() {
      return (
        this.complexes.data &&
        this.complexes.data.length > 0 &&
        this.selected.length === this.complexes.data.length
      );
    }
  },
  watch: {
    queryString: {
      handler: function() {
        this.onPageChanged(this.page);
      }
    }
  },
  methods: {
    fetch() {
      let url = `/v1/complexes${this.queryString}`;

      return ApiService.query(url).then(
        response => (this.complexes = response.data)
      );
    },
    clicked(data) {
      if (!store.state.subject) {
        store.registerModule('subject', () =>
          import('@/store/modules/subject')
        );
      }

      this.$store.commit('subject/setFilters', {
        key: 'filter[complex]',
        value: data.id,
        label: data.label
      });

      return this.$router.push({ name: 'home' });
    },
    async createMatrixFile() {
      if (!this.selected.length) return;

      try {
        await MatrixService.create(this.selected);

        this.notification.type = 'success';
        this.notification.message =
          'Export wordt gemaakt, controleer uw e-mail';

        return;
      } catch (response) {
        this.error = response.data;

        this.notification.type = 'error';
        this.notification.message = 'Export niet gemaakt';
      }
    },
    onSelectAll() {
      if (!this.complexes.data) return;

      const rows = this.$refs.table.tBodies[0].rows;
      const length = this.complexes.data.length;

      if (this.isIndeterminate || this.isSelectAll) {
        for (let i = 0; i < length; i++) {
          rows[i].classList.remove('selected');
        }
        this.selected = [];
      } else {
        for (let i = 0; i < length; i++) {
          rows[i].classList.add('selected');
        }
        this.selected = this.complexes.data.map(row => row.id);
      }
    },
    onSelect(event) {
      event.target.parentNode.parentNode.classList.toggle('selected');
    },
    onPageChanged(page) {
      let query = {
        sort: this.sortDirection + this.sortBy,
        page: page
      };

      if (this.search) {
        query.q = this.search;
      } else {
        delete query.q;
      }

      this.$router.push({
        name: 'complexes',
        query: query
      });
    },
    onSearched(keyword) {
      if (!keyword) {
        this.search = null;
        return this.onPageChanged(1);
      }

      this.search = keyword;
      return this.onPageChanged(1);
    },
    goTo(complex) {
      if (this.$complex.isAllowed('update')) {
        return this.$router.push({
          name: 'complex',
          params: { id: complex.id }
        });
      }

      return this.clicked(complex);
    }
  }
};
</script>

<style scoped>
table tr.selected,
table tr.selected:hover {
  @apply bg-blue-lighter;
  @apply text-grey-darker;
}
</style>
